import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { DrawContext, SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';
import { MagicReportSection } from './MagicReportSection';
import { LIEN_WAIVERS_SECTION_CONFIG } from './sections.config';
import { CommentsSection } from './CommentsSection';
import { getCommentsField } from './section-utils';
import { isCoLienHidden, resolveHiddenCoLienDrawIds } from './co-lien-visibility.util';
import { FieldRow } from './FieldRow';
import { useMagicReport } from '../context/MagicReportContext';
import { Box, ToggleButton, ToggleButtonGroup, TextField, Typography, IconButton, Tooltip, Alert } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { LastMonthFieldToggle } from './LastMonthFieldToggle';
import { SINGLE_SELECT_TOGGLE_SX } from './sections.style';
import { usePrompt } from '../../../AppPrompt';
import { buildLienWaiversAboveTableComment, buildLienWaiversComment } from './lien-waivers-comment.util';
import {
  DrawLienWaiverState,
  LienWaiverStoredStatus,
  drawWaiversChanged,
  legacyDrawWaiverSeed,
  reconcileDrawWaivers,
  serializeDrawWaivers,
  statusLineFor,
  toCommentStatus,
} from './lien-waivers-draws.util';
import { buildLienWaiverContractorLabel, getLienWaiverTableRow } from './lien-waivers-table.util';

interface LienWaiversSectionProps {
  magicReportId: string;
}

/** Falls back to a single synthetic payapp so a report without draws[] still renders. */
const resolveCoveredDraws = (reportState: any): DrawContext[] => {
  if (reportState.draws?.length) return reportState.draws;
  if (!reportState.drawSummary) return [];
  return [
    {
      drawId: reportState.drawSummary.id,
      drawSummary: reportState.drawSummary,
      budgetSummary: reportState.budgetSummary,
    },
  ];
};

const LienWaiversSectionContent: React.FC<{
  data: SectionFieldCollection;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  onBook?: () => number;
}> = ({ data, handleFieldChange, getField, onBook }) => {
  const { reportState } = useMagicReport();
  const commentsField = getCommentsField(data, LIEN_WAIVERS_SECTION_CONFIG.config);

  const coveredDraws = useMemo(() => resolveCoveredDraws(reportState), [reportState]);
  const drawIds = useMemo(() => coveredDraws.map(d => d.drawId), [coveredDraws]);
  const primaryDrawId = drawIds.length > 0 ? drawIds[drawIds.length - 1] : undefined;
  const contractorName = reportState.projectSummary?.contractor || '';

  /**
   * Payapps whose Lien Waivers block is suppressed for this report.
   *
   * Every covered payapp keeps its `DrawLienWaiverState` — nothing is deleted, so unhiding one in
   * a later period brings its answers back — but a hidden payapp must not appear in the rendered
   * blocks, the generated comments, or the archived history recap.
   */
  const hiddenCoLienDrawIds = useMemo(
    () => resolveHiddenCoLienDrawIds(reportState.hiddenCoLienDrawIds, drawIds),
    [reportState.hiddenCoLienDrawIds, drawIds],
  );
  const visibleDrawIds = useMemo(() => drawIds.filter(id => !isCoLienHidden(hiddenCoLienDrawIds, id)), [drawIds, hiddenCoLienDrawIds]);
  // The legacy scalar fields must mirror a payapp the report actually shows, since the export and
  // the backend reset transform fall back to them.
  const visiblePrimaryDrawId = visibleDrawIds.length > 0 ? visibleDrawIds[visibleDrawIds.length - 1] : primaryDrawId;

  const rawDrawWaivers = getField('LienWaiverDraws')?.value;

  /**
   * Per-payapp state, reconciled against the payapps this report covers. When nothing is stored
   * yet the pre-multi-draw scalar fields seed the primary payapp, so existing reports open with
   * their saved answers rather than resetting to the Conditional default.
   */
  const drawWaivers = useMemo(
    () =>
      reconcileDrawWaivers(
        rawDrawWaivers,
        drawIds,
        legacyDrawWaiverSeed(name => getField(name)?.value ?? undefined),
      ),
    [rawDrawWaivers, drawIds, getField],
  );

  /**
   * Writes are funnelled through here so the serialized array is always built from the freshly
   * reconciled list. `mergeRef` absorbs several updates landing in one tick — each payapp block can
   * trigger a write, and without it the last one would clobber the others.
   */
  const mergeRef = useRef<DrawLienWaiverState[] | null>(null);
  const writeDrawWaivers = useCallback(
    (mutate: (current: DrawLienWaiverState[]) => DrawLienWaiverState[]) => {
      const base = mergeRef.current ?? drawWaivers;
      const next = mutate(base);
      mergeRef.current = next;
      handleFieldChange('LienWaiverDraws', { value: serializeDrawWaivers(next) });
    },
    [drawWaivers, handleFieldChange],
  );

  // Once the stored value catches up with what we wrote, stop holding the pending copy.
  useEffect(() => {
    if (mergeRef.current && serializeDrawWaivers(mergeRef.current) === rawDrawWaivers) {
      mergeRef.current = null;
    }
  }, [rawDrawWaivers]);

  const patchDraw = useCallback(
    (drawId: string, patch: Partial<DrawLienWaiverState>) => {
      writeDrawWaivers(current => current.map(entry => (entry.drawId === drawId ? { ...entry, ...patch } : entry)));
    },
    [writeDrawWaivers],
  );

  // Persist the reconciled list when it differs from what is stored (first open, or the covered
  // payapps changed). Also mirrors the primary payapp back onto the legacy scalar fields, which the
  // backend reset transform and any not-yet-migrated consumer still read.
  useEffect(() => {
    if (drawIds.length === 0) return;
    if (drawWaiversChanged(rawDrawWaivers, drawWaivers)) {
      handleFieldChange('LienWaiverDraws', { value: serializeDrawWaivers(drawWaivers) });
    }

    const primary = drawWaivers.find(w => w.drawId === visiblePrimaryDrawId);
    if (!primary) return;
    const mirror: Array<[string, string]> = [
      ['LienWaiverStatus', primary.status],
      ['IsCumulative', primary.isCumulative ? 'true' : 'false'],
      ['ThroughCurrentPeriod', primary.throughCurrentPeriod ? 'true' : 'false'],
      ['LienWaiverIsManuallyEdited', primary.isManuallyEdited ? 'true' : 'false'],
      ['LienWaiverContractor', primary.contractor],
      ['LienWaiverApplicationDate', primary.applicationDate],
      ['LienWaiverApplicationNumber', primary.applicationNumber],
      ['LienWaiverAmount', primary.amount],
    ];
    mirror.forEach(([fieldName, value]) => {
      if ((getField(fieldName)?.value || '') !== value) handleFieldChange(fieldName, { value });
    });
  }, [drawWaivers, rawDrawWaivers, drawIds.length, visiblePrimaryDrawId, getField, handleFieldChange]);

  /** Each payapp's derived row, plus the display values honouring a manual edit. */
  const drawRows = useMemo(
    () =>
      drawWaivers.map(waiver => {
        const context = coveredDraws.find(d => d.drawId === waiver.drawId);
        const isUnconditional = waiver.status === 'Unconditional';
        const tableRow = getLienWaiverTableRow(
          isUnconditional && waiver.isCumulative,
          isUnconditional && waiver.throughCurrentPeriod,
          context?.drawSummary,
          context?.budgetSummary,
        );
        // Only an unconditional waiver carries an amount. A conditional waiver covers this month's
        // application, so it takes the draw's own number rather than the last previous funding's.
        const derived = isUnconditional
          ? tableRow
          : {
              ...tableRow,
              applNumber: `Application #${context?.drawSummary?.applicationNumber || '[This Draw]'}`,
              amount: '',
            };
        const derivedContractor = isUnconditional ? buildLienWaiverContractorLabel(contractorName) : '';
        return {
          waiver,
          name: context?.drawSummary?.name || 'Lien Waiver',
          drawSummary: context?.drawSummary,
          derived,
          derivedContractor,
          // Once manually edited the stored values are shown verbatim — no falling back to the
          // derived value, otherwise clearing a field would immediately refill it.
          display: waiver.isManuallyEdited
            ? {
                contractor: waiver.contractor,
                thruDate: waiver.applicationDate,
                applNumber: waiver.applicationNumber,
                amount: waiver.amount,
              }
            : {
                contractor: derivedContractor,
                thruDate: derived.thruDate,
                applNumber: derived.applNumber,
                amount: derived.amount,
              },
        };
      }),
    [drawWaivers, coveredDraws, contractorName],
  );

  /** The payapps the reader sees. Everything reader-facing below derives from this, not drawRows. */
  const visibleDrawRows = useMemo(
    () => drawRows.filter(({ waiver }) => !isCoLienHidden(hiddenCoLienDrawIds, waiver.drawId)),
    [drawRows, hiddenCoLienDrawIds],
  );

  /**
   * Auto-populates each payapp's detail row from its own draw.
   *
   * The amount is re-derived whenever that payapp's toggles change — including over a manual edit,
   * since the answer to Cumulative?/Thru Curr. Amt? determines it. Between toggle changes a manual
   * amount is left alone; re-deriving it on every render would make the field impossible to type in.
   */
  const toggleSignaturesRef = useRef<Record<string, string>>({});
  useEffect(() => {
    drawRows.forEach(({ waiver, name, derived, derivedContractor }) => {
      const signature = `${waiver.status}|${waiver.isCumulative}|${waiver.throughCurrentPeriod}`;
      const togglesChanged = toggleSignaturesRef.current[waiver.drawId] !== signature;
      toggleSignaturesRef.current[waiver.drawId] = signature;

      const patch: Partial<DrawLienWaiverState> = {};
      // Persisted so the backend can name the history rows it archives on reset — it only sees this
      // JSON, and has no other way to resolve a payapp's name.
      if (waiver.drawName !== name) patch.drawName = name;
      if ((togglesChanged || !waiver.isManuallyEdited) && waiver.amount !== derived.amount) {
        patch.amount = derived.amount;
      }
      if (!waiver.isManuallyEdited) {
        if (waiver.applicationDate !== derived.thruDate) patch.applicationDate = derived.thruDate;
        if (waiver.applicationNumber !== derived.applNumber) patch.applicationNumber = derived.applNumber;
        if (waiver.contractor !== derivedContractor) patch.contractor = derivedContractor;
      }
      if (Object.keys(patch).length > 0) patchDraw(waiver.drawId, patch);
    });
  }, [drawRows, patchDraw]);

  /**
   * Comments are regenerated when the set of payapp statuses changes, and seeded once on first open
   * while still empty. The seed is tracked per field rather than inferred from emptiness — otherwise
   * clearing a comment by hand would immediately re-populate it.
   */
  const statusSignature = useMemo(
    () => visibleDrawRows.map(({ waiver }) => `${waiver.drawId}:${waiver.status}`).join('|'),
    [visibleDrawRows],
  );
  const prevStatusSignatureRef = useRef<string | null>(null);
  const seededRef = useRef({ main: false, above: false });

  useEffect(() => {
    if (visibleDrawRows.length === 0) return;

    const commentWaivers = visibleDrawRows.map(({ waiver, name }) => ({
      drawId: waiver.drawId,
      name,
      status: toCommentStatus(waiver.status),
    }));
    const mainComment = buildLienWaiversComment(commentWaivers);
    const aboveComment = buildLienWaiversAboveTableComment(commentWaivers);

    const statusChanged = prevStatusSignatureRef.current !== null && prevStatusSignatureRef.current !== statusSignature;
    prevStatusSignatureRef.current = statusSignature;

    const mainField = getField('LienWaiverComment');
    if (mainField) {
      const currentMain = mainField.value || '';
      // The seeded default still carries the placeholder, so treat it as unpopulated.
      const needsSeed = !seededRef.current.main && (!currentMain.trim() || currentMain.includes('[G702 Name]'));
      if ((statusChanged || needsSeed) && currentMain !== mainComment) {
        handleFieldChange('LienWaiverComment', { value: mainComment });
      }
      seededRef.current.main = true;
    }

    const aboveField = getField('LienWaiverAboveTableComment');
    if (aboveField) {
      const currentAbove = aboveField.value || '';
      const needsSeed = !seededRef.current.above && !currentAbove.trim();
      if ((statusChanged || needsSeed) && currentAbove !== aboveComment) {
        handleFieldChange('LienWaiverAboveTableComment', { value: aboveComment });
      }
      seededRef.current.above = true;
    }
  }, [statusSignature, visibleDrawRows, getField, handleFieldChange]);

  const handleResetToAuto = (drawId: string) => {
    patchDraw(drawId, { isManuallyEdited: false, contractor: '' });
  };

  const startManualEdit = (waiver: DrawLienWaiverState, patch: Partial<DrawLienWaiverState>) => {
    patchDraw(waiver.drawId, { ...patch, isManuallyEdited: true });
  };

  const isMultiDraw = visibleDrawRows.length > 1;

  return (
    <>
      {reportState.projectSummary?.reviewingSubcontractorLienWaivers === true && (
        <Alert severity='warning' sx={{ mb: 3 }}>
          Remember to review Subcontractor Lien Waivers
        </Alert>
      )}

      {/* Intro line, above every payapp's block */}
      {getField('LienWaiverAboveTableComment') && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ mb: 0.5 }}>
            <LastMonthFieldToggle lastMonthValue={getField('LienWaiverAboveTableComment')?.lastMonth} label='Lien Waiver Intro Comment' />
          </Box>
          <FieldRow
            addToActionItems={getField('LienWaiverAboveTableComment')?.addToActionItems}
            onBookmarkToggle={() => {
              const field = getField('LienWaiverAboveTableComment');
              if (field) handleFieldChange('LienWaiverAboveTableComment', { addToActionItems: !field.addToActionItems });
            }}
            alignStart
          >
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={10}
              value={getField('LienWaiverAboveTableComment')?.value || ''}
              onChange={e => handleFieldChange('LienWaiverAboveTableComment', { value: e.target.value })}
            />
          </FieldRow>
        </Box>
      )}

      {/* One block per covered payapp that isn't hidden */}
      {visibleDrawRows.map(({ waiver, name, display }) => {
        const isUnconditional = waiver.status === 'Unconditional';
        return (
          <Box key={waiver.drawId} id={`lien-waivers-${waiver.drawId}`} sx={{ mb: 4 }}>
            {isMultiDraw && (
              <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5 }}>
                {name} Lien Waiver:
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ width: 21, flexShrink: 0 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                  <Typography variant='body2' sx={{ minWidth: 200 }}>
                    Lien Waivers Received?
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    value={waiver.status}
                    onChange={(_, newValue) => {
                      if (newValue !== null) patchDraw(waiver.drawId, { status: newValue as LienWaiverStoredStatus });
                    }}
                    size='small'
                    sx={SINGLE_SELECT_TOGGLE_SX}
                  >
                    <ToggleButton value='Conditional'>Conditional</ToggleButton>
                    <ToggleButton value='Unconditional'>Unconditional</ToggleButton>
                    <ToggleButton value='Not Received'>Not Received</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Box sx={{ width: 30, flexShrink: 0 }} />
              </Box>

              {isUnconditional && (
                <>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 21, flexShrink: 0 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                      <Typography variant='body2' sx={{ minWidth: 200 }}>
                        Is it cumulative?
                      </Typography>
                      <ToggleButtonGroup
                        exclusive
                        value={waiver.isCumulative ? 'true' : 'false'}
                        onChange={(_, newValue) => {
                          if (newValue !== null) patchDraw(waiver.drawId, { isCumulative: newValue === 'true' });
                        }}
                        size='small'
                        sx={SINGLE_SELECT_TOGGLE_SX}
                      >
                        <ToggleButton value='true'>Yes</ToggleButton>
                        <ToggleButton value='false'>No</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    <Box sx={{ width: 30, flexShrink: 0 }} />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 21, flexShrink: 0 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                      <Typography variant='body2' sx={{ minWidth: 200 }}>
                        Does it go through the current amount?
                      </Typography>
                      <ToggleButtonGroup
                        exclusive
                        value={waiver.throughCurrentPeriod ? 'true' : 'false'}
                        onChange={(_, newValue) => {
                          if (newValue !== null) patchDraw(waiver.drawId, { throughCurrentPeriod: newValue === 'true' });
                        }}
                        size='small'
                        sx={SINGLE_SELECT_TOGGLE_SX}
                      >
                        <ToggleButton value='true'>Yes</ToggleButton>
                        <ToggleButton value='false'>No</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    <Box sx={{ width: 30, flexShrink: 0 }} />
                  </Box>
                </>
              )}
            </Box>

            {/* Detail row for an unconditional waiver; otherwise the status line the client prints */}
            {isUnconditional ? (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 2 }}>
                <Box
                  component='table'
                  sx={{
                    flexGrow: 1,
                    borderCollapse: 'collapse',
                    '& tbody tr:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                    '& tbody tr:nth-of-type(even)': { backgroundColor: '#ffffff' },
                    '& td': {
                      padding: '8px 12px',
                      borderBottom: '1px solid #e0e0e0',
                      fontSize: '0.875rem',
                      verticalAlign: 'middle',
                    },
                    '& td:first-of-type': { fontWeight: 'medium' },
                    '& td:last-of-type': { textAlign: 'right' },
                  }}
                >
                  <tbody>
                    <tr>
                      <td>Contract / Contractor:</td>
                      <td>
                        <TextField
                          size='small'
                          value={display.contractor}
                          onChange={e => startManualEdit(waiver, { contractor: e.target.value })}
                          fullWidth
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>Through Date:</td>
                      <td>
                        <TextField
                          size='small'
                          value={display.thruDate}
                          onChange={e => startManualEdit(waiver, { applicationDate: e.target.value })}
                          fullWidth
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>Application #:</td>
                      <td>
                        <TextField
                          size='small'
                          value={display.applNumber}
                          onChange={e => startManualEdit(waiver, { applicationNumber: e.target.value })}
                          fullWidth
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>Amount:</td>
                      <td>
                        <TextField
                          size='small'
                          value={display.amount}
                          onChange={e => startManualEdit(waiver, { amount: e.target.value })}
                          fullWidth
                        />
                      </td>
                    </tr>
                  </tbody>
                </Box>
                {waiver.isManuallyEdited && (
                  <Tooltip title='Reset to auto-calculated'>
                    <IconButton
                      size='small'
                      onClick={() => handleResetToAuto(waiver.drawId)}
                      sx={{ color: 'text.secondary', mt: 3, flexShrink: 0 }}
                    >
                      <RefreshIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ) : (
              <Typography variant='body2' sx={{ mt: 2, ml: 3.5, fontStyle: 'italic', color: 'text.secondary' }}>
                {statusLineFor(waiver.status)}
              </Typography>
            )}
          </Box>
        );
      })}

      {/* Single generated comment covering every payapp */}
      {getField('LienWaiverComment') && (
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ mb: 0.5 }}>
            <LastMonthFieldToggle lastMonthValue={getField('LienWaiverComment')?.lastMonth} label='Lien Waiver Comment' />
          </Box>
          <FieldRow
            addToActionItems={getField('LienWaiverComment')?.addToActionItems}
            onBookmarkToggle={() => {
              const field = getField('LienWaiverComment');
              if (field) handleFieldChange('LienWaiverComment', { addToActionItems: !field.addToActionItems });
            }}
            alignStart
          >
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={20}
              value={getField('LienWaiverComment')?.value || ''}
              onChange={e => handleFieldChange('LienWaiverComment', { value: e.target.value })}
            />
          </FieldRow>
        </Box>
      )}

      {commentsField && (
        <Box sx={{ mt: 3 }}>
          <CommentsSection
            commentsField={commentsField}
            onChange={updated => handleFieldChange('Comments', updated)}
            title='Comments'
            onBook={onBook}
          />
        </Box>
      )}
    </>
  );
};

export const LienWaiversSection: React.FC<LienWaiversSectionProps> = ({ magicReportId }) => {
  const { reportState } = useMagicReport();
  const { getConfirmation } = usePrompt();

  return (
    <MagicReportSection
      magicReportId={magicReportId}
      sectionConfig={LIEN_WAIVERS_SECTION_CONFIG}
      customization={{
        renderContent: (data, handleFieldChange, getField, onBook) => (
          <LienWaiversSectionContent data={data} handleFieldChange={handleFieldChange} getField={getField} onBook={onBook} />
        ),
        onBeforeComplete: () => {
          if (reportState.projectSummary?.reviewingSubcontractorLienWaivers !== true) return true;
          return getConfirmation({
            heading: 'Confirm Lien Waiver Review',
            description: 'Have you reviewed subcontractor lien waivers?',
            confirmButton: { label: 'Yes' },
            cancelButton: { label: 'No' },
          });
        },
      }}
    />
  );
};
